import { useState, useEffect, lazy, Suspense, useMemo, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { ShoppingCart, Minus, Plus, Trash2, ExternalLink, Loader2, Truck, ShieldCheck, Heart, ArrowLeft, Info, Coffee } from "lucide-react";
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "@/components/ui/tooltip";
import { useCartStore } from "@/stores/cartStore";
import { applyDiscountCodesToCart, syncTipLine } from "@/lib/shopify";
import { trackEvent } from "@/lib/analytics";
import { calculateBundle, getPromoHints, getBundleDiscountCode, UNIT_PRICE } from "@/lib/bundlePricing";
import { Input } from "@/components/ui/input";
import { CartItemBadge } from "@/components/cart/CartItemBadge";

// Confetti chargé à la demande (uniquement après clic checkout) — réduit le JS initial.
const ConfettiCanvas = lazy(() =>
  import("@/components/ConfettiCanvas").then((m) => ({ default: m.ConfettiCanvas })),
);

// Image servie depuis /public — n'entre pas dans le bundle JS et profite du cache HTTP.
const checkoutBg = "/assets/checkout-bg-tunisian.webp";

const QUICK_AMOUNTS = [1, 2, 5, 100, 500];
const MAX_TIP = 10000;
const FREE_SHIPPING_CODE = "LIVRAISON-GRATUITE-69";
const FREE_SHIPPING_THRESHOLD = 69;

export const CartDrawer = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [tipAmount, setTipAmount] = useState<number | null>(null);
  const [customAmount, setCustomAmount] = useState("");
  const [nickname, setNickname] = useState("");
  const [showConfetti, setShowConfetti] = useState(false);

  // Sélecteurs Zustand scoppés → évite les re-renders inutiles quand d'autres champs du store changent.
  const items = useCartStore((s) => s.items);
  const isLoading = useCartStore((s) => s.isLoading);
  const isSyncing = useCartStore((s) => s.isSyncing);
  const updateQuantity = useCartStore((s) => s.updateQuantity);
  const getCheckoutUrl = useCartStore((s) => s.getCheckoutUrl);
  const syncCart = useCartStore((s) => s.syncCart);

  // Tous les agrégats dérivent de `items` — un seul useMemo, recalculé seulement si items change.
  const totals = useMemo(() => {
    let totalItems = 0;
    let eligibleStickerCount = 0;
    let nonStickerSubtotal = 0;
    let donationSubtotal = 0;
    for (const item of items) {
      const price = parseFloat(item.price.amount) * item.quantity;
      totalItems += item.quantity;
      if (item.productType === "sticker") {
        eligibleStickerCount += item.quantity;
      } else {
        nonStickerSubtotal += price;
        if (item.productType === "donation") donationSubtotal += price;
      }
    }
    const bundle = calculateBundle(eligibleStickerCount);
    const stickerSubtotal = bundle?.totalBundlePrice ?? 0;
    const stickerFullPrice = bundle?.totalFullPrice ?? 0;
    const bundleDiscount = bundle?.totalDiscount ?? 0;
    const subtotal = parseFloat((stickerSubtotal + nonStickerSubtotal).toFixed(2));
    const shippingEligibleTotal = parseFloat((subtotal - donationSubtotal).toFixed(2));
    return {
      totalItems,
      eligibleStickerCount,
      nonStickerSubtotal,
      donationSubtotal,
      bundle,
      stickerFullPrice,
      bundleDiscount,
      subtotal,
      shippingEligibleTotal,
      promoHints: getPromoHints(eligibleStickerCount),
      freeShipping: shippingEligibleTotal >= FREE_SHIPPING_THRESHOLD,
    };
  }, [items]);

  const { totalItems, eligibleStickerCount, nonStickerSubtotal, donationSubtotal, bundle, stickerFullPrice,
    bundleDiscount, subtotal, shippingEligibleTotal, promoHints, freeShipping } = totals;

  useEffect(() => { if (isOpen) syncCart(); }, [isOpen, syncCart]);

  const handleCheckout = async () => {
    const checkoutUrl = getCheckoutUrl();
    const { cartId } = useCartStore.getState();
    if (!checkoutUrl || !cartId) {
      trackEvent("checkout_click", { outcome: "no_cart" });
      return;
    }

    const isStandalone =
      typeof window !== "undefined" &&
      (window.matchMedia?.("(display-mode: standalone)").matches ||
        (window.navigator as Navigator & { standalone?: boolean }).standalone === true);

    const isPreviewHost =
      typeof window !== "undefined" &&
      (window.location.hostname.includes("id-preview--") ||
        window.location.hostname.includes("lovableproject.com"));

    let isInIframe = false;
    try {
      isInIframe = typeof window !== "undefined" && window.self !== window.top;
    } catch {
      isInIframe = true;
    }

    const baseProps = {
      items: totalItems,
      subtotal,
      tip: tipAmount ?? 0,
      free_shipping: shippingEligibleTotal >= FREE_SHIPPING_THRESHOLD,
      standalone: isStandalone,
      iframe: isInIframe,
      preview: isPreviewHost,
    };

    trackEvent("checkout_click", baseProps);
    // Standard funnel event (matches GA4 / Plausible Ecommerce conventions)
    trackEvent("begin_checkout", {
      items: totalItems,
      value: totalPrice,
      tip: tipAmount ?? 0,
      free_shipping: shippingEligibleTotal >= FREE_SHIPPING_THRESHOLD,
    });

    if (isPreviewHost || isInIframe) {
      trackEvent("checkout_redirect_bridge", baseProps);
      const redirectUrl = new URL("/checkout-redirect", window.location.origin);
      redirectUrl.searchParams.set("tip", (tipAmount ?? 0).toString());
      window.location.assign(redirectUrl.toString());
      return;
    }

    const checkoutWindow = isStandalone ? null : window.open("about:blank", "_blank");
    const popupBlocked = !isStandalone && (!checkoutWindow || checkoutWindow.closed);

    let finalCheckoutUrl = checkoutUrl;
    try {
      await syncTipLine(cartId, tipAmount ?? 0);
      const codes: string[] = [];
      const bundleCode = getBundleDiscountCode(eligibleStickerCount);
      if (bundleCode) codes.push(bundleCode);
      if (shippingEligibleTotal >= FREE_SHIPPING_THRESHOLD) codes.push(FREE_SHIPPING_CODE);
      const result = await applyDiscountCodesToCart(cartId, codes);
      if (result.checkoutUrl) finalCheckoutUrl = result.checkoutUrl;
    } catch (error) {
      console.error("Checkout preparation failed:", error);
      trackEvent("checkout_prepare_error", {
        ...baseProps,
        message: (error as Error)?.message ?? "unknown",
      });
    }

    if (checkoutWindow && !checkoutWindow.closed) {
      checkoutWindow.location.href = finalCheckoutUrl;
      trackEvent("checkout_open_success", { ...baseProps, mode: "new_tab" });
    } else {
      window.location.href = finalCheckoutUrl;
      trackEvent("checkout_open_success", {
        ...baseProps,
        mode: "same_tab",
        popup_blocked: popupBlocked,
      });
    }

    setShowConfetti(true);
    setTimeout(() => {
      setShowConfetti(false);
      setTipAmount(null);
      setCustomAmount("");
      setNickname("");
      setIsOpen(false);
    }, 1800);
  };

  // Pourboire libre, plafonné à 10 000 $.
  const maxTip = MAX_TIP;

  const handleQuickAmount = (amount: number) => {
    if (tipAmount === amount) {
      setTipAmount(null);
      setCustomAmount("");
    } else {
      const capped = Math.min(amount, maxTip);
      setTipAmount(capped);
      setCustomAmount(capped.toString());
    }
  };

  const handleCustomAmountChange = (value: string) => {
    setCustomAmount(value);
    const num = parseFloat(value);
    if (!isNaN(num) && num > 0) {
      const capped = Math.min(num, maxTip);
      setTipAmount(capped);
      if (capped !== num) setCustomAmount(capped.toString());
    } else if (value === "") {
      setTipAmount(null);
    }
  };

  const totalPrice = subtotal + (tipAmount ?? 0);

  return (
    <>
    {showConfetti && (
      <Suspense fallback={null}>
        <ConfettiCanvas active={showConfetti} />
      </Suspense>
    )}
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetTrigger asChild>
        <Button variant="outline" size="icon" aria-label="Panier" className="relative border-primary/30 hover:border-primary hover:bg-primary/5">
          <ShoppingCart className="h-5 w-5 text-primary" />
          {totalItems > 0 && (
            <Badge className="absolute -top-2 -right-2 h-5 w-5 rounded-full p-0 flex items-center justify-center text-xs bg-primary text-primary-foreground animate-in zoom-in duration-200">
              {totalItems}
            </Badge>
          )}
        </Button>
      </SheetTrigger>
      <SheetContent className="w-full sm:max-w-md flex flex-col h-full p-0 border-l-0 overflow-hidden">
        <img src={checkoutBg} alt="" loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/70 backdrop-blur-[2px]" />

        {/* Decorative top border */}
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[hsl(210,65%,38%)] via-primary to-[hsl(38,50%,78%)] z-20" />

        <div className="relative z-10 flex flex-col h-full">
          {/* Header - compact */}
          <div className="flex-shrink-0 px-5 py-4 border-b border-white/10">
            <SheetHeader>
              <SheetTitle className="font-display text-lg flex items-center gap-2 text-white">
                <Button variant="ghost" size="icon" className="h-7 w-7 text-white/70 hover:text-white hover:bg-white/10 -ml-1" onClick={() => setIsOpen(false)}>
                  <ArrowLeft className="h-4 w-4" />
                </Button>
                <ShoppingCart className="w-4 h-4 text-[hsl(38,50%,78%)]" />
                Mon Panier
                <span className="ml-auto text-xs font-body text-white/40">🇹🇳</span>
              </SheetTitle>
              <SheetDescription className="font-body text-white/50 text-xs">
                {totalItems === 0 ? "Ton panier est vide ✨" : `${totalItems} article${totalItems !== 1 ? 's' : ''}`}
              </SheetDescription>
            </SheetHeader>
          </div>

          <div className="flex flex-col flex-1 px-4 pt-3 min-h-0">
            {items.length === 0 ? (
              <div className="flex-1 flex items-center justify-center">
                <div className="text-center space-y-3">
                  <div className="w-16 h-16 rounded-full bg-white/10 flex items-center justify-center mx-auto">
                    <ShoppingCart className="h-8 w-8 text-white/30" />
                  </div>
                  <div>
                    <p className="text-white/70 font-body">Ton panier est vide</p>
                    <p className="text-xs text-white/40 mt-1">Découvre nos adhésifs tunisiens 🇹🇳</p>
                  </div>
                </div>
              </div>
            ) : (
              <>



                {/* Shipping bar - slim */}
                <div className="mb-3 px-3 py-2 rounded-lg bg-white/90 border border-white/20 shadow-md">
                  <div className="flex items-center gap-2">
                    <Truck className="w-3.5 h-3.5 text-primary shrink-0" />
                    <span className="text-[11px] font-body text-foreground flex-1">
                      {freeShipping
                        ? "✅ Livraison gratuite !"
                        : `Plus que ${(FREE_SHIPPING_THRESHOLD - shippingEligibleTotal).toFixed(2)}$ pour la livraison gratuite`
                      }
                    </span>
                  </div>
                  <div className="w-full bg-muted rounded-full h-1 overflow-hidden mt-1.5">
                    <div
                      className="h-full bg-primary rounded-full transition-all duration-500 ease-out"
                      style={{ width: `${Math.min(100, (shippingEligibleTotal / FREE_SHIPPING_THRESHOLD) * 100)}%` }}
                    />
                  </div>
                </div>

                {/* Items list - cleaner cards */}
                <div className="flex-1 overflow-y-auto pr-1 min-h-0 space-y-2">
                  {items.map((item) => {
                    const image = item.product.node.images?.edges?.[0]?.node;
                    const itemTotal = parseFloat(item.price.amount) * item.quantity;
                    const isDonation = item.productType === 'donation';
                    return (
                      <div
                        key={`${item.variantId}__${item.productType}`}
                        className={`flex gap-3 p-3 rounded-xl shadow-md hover:shadow-lg transition-shadow ${
                          isDonation
                            ? "bg-rose-50 border-2 border-rose-400 border-l-[6px] border-l-rose-600 ring-1 ring-rose-200"
                            : "bg-white border border-white/15"
                        }`}
                      >
                        {/* Image */}
                        <div className={`w-16 h-16 sm:w-14 sm:h-14 rounded-md overflow-hidden flex-shrink-0 border ${isDonation ? "border-rose-300" : "border-border/50"}`}>
                          {image ? (
                            <img src={image.url} alt={item.product.node.title} className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center bg-muted">
                              <ShoppingCart className="w-5 h-5 text-muted-foreground" />
                            </div>
                          )}
                        </div>
                        {/* Details */}
                        <div className="flex-1 min-w-0 flex flex-col justify-between py-0.5">
                          <div>
                            <CartItemBadge variant={isDonation ? "donation" : "sticker"} className="mb-1" />
                            <h4 className={`font-display font-semibold text-sm sm:text-xs truncate leading-tight ${isDonation ? "text-rose-900" : "text-foreground"}`}>{item.product.node.title}</h4>
                            {item.selectedOptions.length > 0 && item.selectedOptions[0].value !== "Default Title" && (
                              <p className="text-[11px] sm:text-[10px] text-muted-foreground mt-0.5 truncate">
                                {item.selectedOptions.map(o => o.value).join(' · ')}
                              </p>
                            )}
                          </div>
                          <div className="flex items-center justify-between mt-2">
                            <div className="flex items-center bg-muted/80 rounded-md border border-border/50">
                              <Button variant="ghost" size="icon" className="h-8 w-8 sm:h-6 sm:w-6 hover:bg-destructive/10 hover:text-destructive rounded-md" onClick={() => updateQuantity(item.variantId, item.productType, item.quantity - 1)} disabled={isLoading}>
                                {item.quantity === 1 ? <Trash2 className="h-3.5 w-3.5 sm:h-2.5 sm:w-2.5" /> : <Minus className="h-3.5 w-3.5 sm:h-2.5 sm:w-2.5" />}
                              </Button>
                              <span className="w-8 sm:w-6 text-center text-sm sm:text-xs font-semibold text-foreground">{item.quantity}</span>
                              <Button variant="ghost" size="icon" className="h-8 w-8 sm:h-6 sm:w-6 hover:bg-primary/10 hover:text-primary rounded-md" onClick={() => updateQuantity(item.variantId, item.productType, item.quantity + 1)} disabled={isLoading}>
                                <Plus className="h-3.5 w-3.5 sm:h-2.5 sm:w-2.5" />
                              </Button>
                            </div>
                            <span className={`font-display font-bold text-sm sm:text-xs ${isDonation ? "text-rose-700" : "text-primary"}`}>
                              {itemTotal.toFixed(2)}$
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Footer */}
                <div className="flex-shrink-0 space-y-2.5 pt-3 border-t border-white/10 mt-3 pb-5">
                  {/* Pourboire boutique (distinct du Don Solidaire produit) - thème café/or */}
                  <div className="rounded-xl bg-amber-50 border-2 border-amber-400 border-l-[6px] border-l-amber-700 ring-1 ring-amber-200 shadow-md p-3">
                    <div className="flex items-center gap-1.5 mb-1.5 flex-wrap">
                      <CartItemBadge variant="tip" />
                      <span className="font-display font-semibold text-[12px] sm:text-[11px] text-amber-900">à la boutique</span>
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <button type="button" aria-label="Info pourboire" className="ml-auto h-7 w-7 -mr-1 inline-flex items-center justify-center rounded-full text-amber-800 hover:bg-amber-100">
                              <Info className="w-4 h-4" />
                            </button>
                          </TooltipTrigger>
                          <TooltipContent side="top" className="max-w-[260px] text-xs">
                            <p>Le pourboire (إكرامية) est versé intégralement à l'équipe EL FAZA pour soutenir la boutique.</p>
                            <p className="mt-1">Il est distinct du Don Solidaire (تبرع تضامني), produit séparé dédié aux familles tunisiennes.</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                    <p className="text-[11px] sm:text-[10px] text-amber-900/80 mb-2 leading-snug">
                      Coup de pouce pour l'équipe EL FAZA · <span className="italic">différent du Don Solidaire</span> 🇹🇳
                    </p>
                    <div className="flex gap-1 mb-1.5">
                      {QUICK_AMOUNTS.map((amount) => {
                        const disabled = amount > maxTip;
                        return (
                          <button
                            key={amount}
                            onClick={() => handleQuickAmount(amount)}
                            disabled={disabled}
                            className={`flex-1 py-1 rounded-md text-[11px] font-display font-bold transition-all duration-200 border ${
                              tipAmount === amount
                                ? "bg-amber-700 text-white border-amber-700 shadow-sm"
                                : "bg-white/70 text-amber-900 border-amber-200 hover:border-amber-400"
                            } ${disabled ? "opacity-40 cursor-not-allowed hover:border-amber-200" : ""}`}
                          >
                            {amount}$
                          </button>
                        );
                      })}
                      <Input
                        type="number"
                        min="0.5"
                        max={maxTip}
                        step="0.5"
                        placeholder="Autre"
                        value={customAmount}
                        onChange={(e) => handleCustomAmountChange(e.target.value)}
                        className="flex-1 h-[26px] text-[11px] bg-background border-border min-w-0 px-2"
                      />
                    </div>
                    <p className="text-[9px] text-muted-foreground/70 italic mt-0.5">Max {maxTip}$ (montant de la commande) · Aucune taxe appliquée</p>
                    {tipAmount && tipAmount > 0 && (
                      <div className="flex gap-1.5 items-center">
                        <Input
                          type="text"
                          placeholder="Pseudo (ou Anonyme)"
                          maxLength={50}
                          value={nickname}
                          onChange={(e) => setNickname(e.target.value)}
                          className="h-6 text-[11px] bg-background border-border flex-1 px-2"
                        />
                        <span className="text-[11px] text-amber-800 font-body whitespace-nowrap flex items-center gap-0.5">
                          <Coffee className="w-2.5 h-2.5" />{tipAmount.toFixed(0)}$ ☕
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Promo hints */}
                  {promoHints.length > 0 && (
                    <div className="rounded-lg bg-primary/10 border border-primary/20 px-3 py-2">
                      {promoHints.map((hint, i) => (
                        <p key={i} className="text-[11px] font-body text-primary font-semibold">
                          🎁 {hint.text} <span className="text-accent">(économise {hint.saved})</span>
                        </p>
                      ))}
                    </div>
                  )}

                  {/* Totals - clean */}
                  <div className="bg-white/90 rounded-lg p-3 shadow-md space-y-1.5">
                    {/* Bundle breakdown */}
                    {bundle && bundle.lines.length > 0 && (
                      <>
                        {bundle.lines.map((line, i) => (
                          <div key={i} className="flex justify-between items-center text-[11px] text-muted-foreground">
                            <span className="font-body">{line.qty > 1 ? `${line.qty}× ` : ''}{line.label}</span>
                            <span className="text-foreground">{line.lineTotal.toFixed(2)}$</span>
                          </div>
                        ))}
                        {bundleDiscount > 0 && (
                          <div className="flex justify-between items-center text-[11px] text-accent font-semibold">
                            <span className="font-body">🎉 Économie pack</span>
                            <span>-{bundleDiscount.toFixed(2)}$</span>
                          </div>
                        )}
                        {(() => {
                          const code = getBundleDiscountCode(eligibleStickerCount);
                          if (!code) return null;
                          return (
                            <div className="flex justify-between items-center text-[10px]">
                              <span className="font-body text-muted-foreground">Code appliqué auto</span>
                              <span className="px-1.5 py-0.5 rounded bg-accent/15 border border-accent/30 text-accent font-mono font-bold tracking-tight">
                                {code}
                              </span>
                            </div>
                          );
                        })()}
                        <div className="border-t border-border/50 my-0.5" />
                      </>
                    )}
                    <div className="flex justify-between items-center text-xs text-muted-foreground">
                      <span className="font-body">Sous-total articles ({totalItems})</span>
                      <div className="flex items-center gap-1.5">
                        {bundleDiscount > 0 && (
                          <span className="line-through text-muted-foreground/50 text-[10px]">{(stickerFullPrice + nonStickerSubtotal - donationSubtotal).toFixed(2)}$</span>
                        )}
                        <span className="text-foreground">{(subtotal - donationSubtotal).toFixed(2)}$</span>
                      </div>
                    </div>
                    {donationSubtotal > 0 && (
                      <div className="flex justify-between items-center text-xs">
                        <CartItemBadge variant="donation" size="sm" />
                        <span className="text-rose-700 font-semibold">+ {donationSubtotal.toFixed(2)}$</span>
                      </div>
                    )}
                    {tipAmount && tipAmount > 0 && (
                      <div className="flex justify-between items-center text-xs">
                        <CartItemBadge variant="tip" size="sm" />
                        <span className="text-amber-800 font-semibold">+ {tipAmount.toFixed(2)}$</span>
                      </div>
                    )}
                    <div className="flex justify-between items-center text-xs text-muted-foreground">
                      <span className="font-body">Livraison</span>
                      <span className={freeShipping ? "text-accent font-semibold" : "text-foreground"}>
                        {freeShipping ? "Gratuite ✨" : "Au checkout"}
                      </span>
                    </div>
                    <div className="flex justify-between items-center pt-1.5 border-t border-border">
                      <span className="text-sm font-display font-bold text-foreground">Total</span>
                      <span className="text-lg font-display font-bold text-primary">
                        {totalPrice.toFixed(2)}$
                      </span>
                    </div>
                  </div>

                  <Button
                    onClick={handleCheckout}
                    className="w-full bg-primary text-primary-foreground hover:bg-primary/90 shadow-lg shadow-primary/25 font-display text-sm"
                    size="default"
                    disabled={items.length === 0 || isLoading || isSyncing}
                  >
                    {isLoading || isSyncing ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <>
                        <ExternalLink className="w-3.5 h-3.5 mr-1.5" />
                        Passer la commande
                      </>
                    )}
                  </Button>

                  <div className="flex items-center justify-center gap-3 text-[10px] text-white/50 pt-0.5">
                    <span className="flex items-center gap-1"><ShieldCheck className="w-2.5 h-2.5" /> Sécurisé</span>
                    <span className="text-[hsl(38,50%,78%)]">✦</span>
                    <span>🇨🇦 Canada</span>
                    <span className="text-[hsl(38,50%,78%)]">✦</span>
                    <span>🇹🇳 Tunisie</span>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </SheetContent>
    </Sheet>
    </>
  );
};
